import type { DailyWeatherPoint } from '../../utils/climatePresets';

const DEFAULT_LATITUDE_DEG = 50;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const round = (value: number, digits: number): number => {
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

export const estimateAstronomicalDaylightHours = (dayOfYear: number, latitudeDeg = DEFAULT_LATITUDE_DEG): number => {
  const declination = 23.45 * Math.sin(((360 / 365) * (dayOfYear - 81)) * (Math.PI / 180));
  const latitudeRad = latitudeDeg * (Math.PI / 180);
  const declinationRad = declination * (Math.PI / 180);
  const cosHourAngle = -Math.tan(latitudeRad) * Math.tan(declinationRad);

  if (cosHourAngle < -1) return 24;
  if (cosHourAngle > 1) return 0;

  const hourAngle = Math.acos(cosHourAngle) * (180 / Math.PI);
  return round(hourAngle / 7.5, 1);
};

export const cloudCoverFromOutdoorSunHours = (
  dayOfYear: number,
  outdoorSunHours: number,
  latitudeDeg = DEFAULT_LATITUDE_DEG,
): number => {
  const daylightHours = estimateAstronomicalDaylightHours(dayOfYear, latitudeDeg);
  if (daylightHours <= 0) return 0.5;

  return round(clamp(1 - (clamp(outdoorSunHours, 0, daylightHours) / daylightHours), 0, 1), 2);
};

export const outdoorSunHoursFromCloudCover = (
  dayOfYear: number,
  cloudCover: number,
  latitudeDeg = DEFAULT_LATITUDE_DEG,
): number => {
  const daylightHours = estimateAstronomicalDaylightHours(dayOfYear, latitudeDeg);
  const normalizedCloudCover = clamp(cloudCover, 0, 1);
  return round(Math.max(0, daylightHours * (1 - normalizedCloudCover)), 1);
};

export const applyCloudCoverToPoint = (point: DailyWeatherPoint, cloudCover: number): DailyWeatherPoint => {
  const normalizedCloudCover = round(clamp(cloudCover, 0, 1), 2);
  const astronomicalDaylightHours = point.astronomicalDaylightHours ?? estimateAstronomicalDaylightHours(point.dayOfYear);
  return {
    ...point,
    astronomicalDaylightHours,
    cloudCover: normalizedCloudCover,
    // Display-only preview; the server re-derives sun hours from cloud × its own day length.
    outdoorSunHours: round(Math.max(0, astronomicalDaylightHours * (1 - normalizedCloudCover)), 1),
    cloudEdited: true,
  };
};

/**
 * `extra` payload for a weather point sent to the server.
 *
 * The astronomical day length is pure geometry (date × the save's latitude) and is
 * computed server-side — a weather map must never carry it (an edited point once
 * dragged a 50°N day into a 25°N save). Cloud cover is sent only when the player
 * actually edited it; sun hours and PPFD are then dropped so the server derives
 * them from the new cloud cover instead of keeping stale values.
 */
export const buildWeatherPointExtra = (point: DailyWeatherPoint): Record<string, unknown> => {
  const { astronomicalDaylightHours: _dayLength, ...carried } = point.extra ?? {};
  const extra: Record<string, unknown> = {
    ...carried,
    precipitationHours: point.precipitationHours ?? null,
    weatherCode: point.weatherCode ?? null,
    thunderstormCode: point.thunderstormCode ?? null,
    sourceType: point.source ?? 'climate_norm',
  };
  if (point.cloudEdited) {
    extra.cloudCover = point.cloudCover;
    delete extra.outdoorSunHours;
    delete extra.solarPpfdUmol;
  }
  return extra;
};

export const isoDateFromDayOfYear = (dayOfYear: number, year = new Date().getUTCFullYear()): string => {
  const date = new Date(Date.UTC(year, 0, dayOfYear));
  return date.toISOString().slice(0, 10);
};

export const dayOfYearFromIsoDate = (value?: string | null): number | null => {
  if (!value) return null;
  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  if (!match) return null;

  const date = new Date(`${match[0]}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;

  const startOfYear = Date.UTC(date.getUTCFullYear(), 0, 1);
  const currentDay = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return Math.floor((currentDay - startOfYear) / MS_PER_DAY) + 1;
};

export const utcYearFromIsoDate = (
  value?: string | null,
  fallbackYear = new Date().getUTCFullYear(),
): number => {
  if (!value) return fallbackYear;
  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  if (!match) return fallbackYear;

  const date = new Date(`${match[0]}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return fallbackYear;

  return date.getUTCFullYear();
};